import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Plus,
  Minus,
  CheckSquare,
  Database,
  Layers,
  Crosshair,
  PenLine,
  Send,
  CalendarCheck,
  Users,
  Wrench,
  LineChart,
  Trophy,
  Award,
} from "lucide-react";
import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import useReveal from "../../lib/useReveal";

const CALENDLY = "https://cal.com/rggsales/30min";

/* --------------------------------- Pieces --------------------------------- */

const Eyebrow = ({ children, tone = "green" }) =>
  tone === "green" ? (
    <span className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3.5 py-1.5">
      <span className="eyebrow text-[#16A06A]">{children}</span>
    </span>
  ) : (
    <span className="eyebrow text-slate-400">{children}</span>
  );

const PrimaryCTA = ({ children = "Book a Strategy Call", testid }) => (
  <a
    href={CALENDLY}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group inline-flex items-center justify-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-6 py-3.5 text-[15px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_rgba(22,160,106,0.35)] transition-all"
  >
    {children}
    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
);

const MiniStat = ({ label, value }) => (
  <div className="rounded-lg bg-white/95 backdrop-blur border border-slate-200 px-3 py-2 card-soft">
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
      {label}
    </div>
    <div className="text-[18px] font-bold text-[#0A1628] leading-tight">{value}</div>
  </div>
);

/* ---------------------------------- Hero ---------------------------------- */

const Hero = () => (
  <section data-testid="lg-hero" className="relative pt-32 lg:pt-40 pb-20 lg:pb-24 overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[rgba(22,160,106,0.08)] blur-3xl" />
      <div className="absolute top-32 -left-40 h-[420px] w-[420px] rounded-full bg-[rgba(10,22,40,0.05)] blur-3xl" />
    </div>

    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <Link
        to="/services"
        data-testid="lg-hero-back"
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#16A06A] hover:text-[#0E7C53] reveal"
      >
        <ArrowLeft size={14} />
        All services
      </Link>

      <div className="mt-5 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 reveal">
          <Eyebrow>Lead Generation</Eyebrow>
          <h1 className="h1-hero mt-5 text-[#0A1628]">
            A Predictable Pipeline of{" "}
            <span className="text-[#16A06A]">Qualified Meetings.</span>
          </h1>
          <p className="mt-6 text-[18px] leading-[1.65] text-slate-500 max-w-[620px]">
            Done-for-you outbound that fills your calendar with the right
            buyers. RevGen builds the lists, writes the messaging, runs
            multichannel campaigns, and books qualified meetings — so your team
            only shows up to sell.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
            {["Built lists", "Multichannel outreach", "Meetings booked"].map((c) => (
              <div key={c} className="inline-flex items-center gap-2 text-[14px] text-slate-600">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#E7F6EE]">
                  <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
                </span>
                {c}
              </div>
            ))}
          </div>

          <div className="mt-8">
            <PrimaryCTA testid="lg-hero-cta" />
          </div>
        </div>

        <div className="lg:col-span-5 reveal">
          <div className="rounded-2xl bg-white card-soft border border-slate-200 overflow-hidden">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1552581234-26160f608093"
                alt="Sales team reviewing pipeline"
                loading="eager"
                className="w-full h-[400px] lg:h-[480px] object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white/85 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------------------------- Stats --------------------------------- */

const STATS = [
  {
    title: "Built lists, not bought lists",
    body: "Your target accounts researched and verified, not scraped in bulk.",
  },
  {
    title: "Follow-up that happens",
    body: "Sequences run on schedule whether or not anyone is chasing them.",
  },
  {
    title: "Everything in your pipeline",
    body: "Every touch logged where you can see it, so nothing is anecdotal.",
  },
  {
    title: "We run it, you approve it",
    body: "You see the messaging before it goes out.",
  },
];

const Stats = () => (
  <section
    data-testid="lg-stats"
    className="py-16 lg:py-20 bg-[#F8FAFC] border-y border-slate-200"
  >
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
        {STATS.map((s, i) => (
          <div
            key={s.title}
            className="reveal text-left lg:pl-8 lg:border-l border-slate-200 first:border-l-0 first:pl-0"
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <div className="text-[17px] lg:text-[18px] font-bold text-[#0A1628] leading-snug">
              {s.title}
            </div>
            <div className="mt-3 text-[14px] leading-relaxed text-slate-500">
              {s.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------------- Problem -------------------------------- */

const PROBLEMS = [
  {
    icon: CheckSquare,
    title: "Prospecting Always Loses",
    body:
      "Reps deprioritize cold outreach in favor of active deals, so the pipeline dries up two quarters later.",
  },
  {
    icon: Database,
    title: "Lists Go Stale Fast",
    body:
      "Bad data and generic targeting burn your domain reputation and waste sending capacity.",
  },
  {
    icon: Layers,
    title: "No System, No Predictability",
    body:
      "Without a repeatable engine, pipeline swings wildly month to month and forecasting becomes a guess.",
  },
];

const Problem = () => (
  <section data-testid="lg-problem" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Issue</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Great Reps, <span className="text-[#16A06A]">Empty Calendar.</span>
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          Most teams don't have a closing problem — they have a top-of-funnel
          problem. Prospecting gets squeezed out by everything else.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {PROBLEMS.map((p, i) => {
          const Icon = p.icon;
          return (
            <article
              key={p.title}
              data-testid={`lg-problem-card-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#E7F6EE] text-[#16A06A]">
                <Icon size={18} strokeWidth={2.4} />
              </div>
              <h3 className="mt-5 text-[19px] font-semibold text-[#0A1628] leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-500">{p.body}</p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------ What We Do ------------------------------- */

const CAPABILITIES = [
  {
    icon: Crosshair,
    title: "Targeting & List Building",
    body:
      "We define your ideal customer profile, build verified contact lists, and enrich every record so you reach the right person at the right account.",
  },
  {
    icon: PenLine,
    title: "Messaging & Copywriting",
    body:
      "Personalized, value-led sequences written to start conversations — not to get marked as spam.",
  },
  {
    icon: Send,
    title: "Multichannel Outreach",
    body:
      "Coordinated email, LinkedIn, and calling cadences that meet buyers where they are and compound response rates.",
  },
  {
    icon: CalendarCheck,
    title: "Meeting Booking & Handoff",
    body:
      "We qualify replies, handle scheduling, and hand off sales-ready meetings straight to your reps' calendars.",
  },
];

const WhatWeDo = () => (
  <section data-testid="lg-capabilities" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Solution</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          We Run Your Outbound, <span className="text-[#16A06A]">End to End.</span>
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          A complete lead-generation engine — list building, outreach, and
          follow-up run by a trained operator inside your tools.
        </p>
      </div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {CAPABILITIES.map((c, i) => {
          const Icon = c.icon;
          return (
            <article
              key={c.title}
              data-testid={`lg-capability-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-6 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={18} />
              </div>
              <h3 className="mt-5 text-[18px] font-semibold text-[#0A1628]">{c.title}</h3>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-500">{c.body}</p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------ How It Works ------------------------------ */

const STEPS = [
  {
    n: "01",
    title: "Define the Target",
    body: "We align on your ICP, offer, and the personas worth pursuing.",
  },
  {
    n: "02",
    title: "Build & Warm",
    body:
      "We build verified lists and warm up sending infrastructure to protect deliverability.",
  },
  {
    n: "03",
    title: "Launch Campaigns",
    body:
      "Multichannel sequences go live, with continuous testing on messaging and targeting.",
  },
  {
    n: "04",
    title: "Book & Optimize",
    body:
      "Qualified meetings land on your calendar; we report weekly and tune for higher conversion.",
  },
];

const HowItWorks = () => (
  <section data-testid="lg-how" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Process</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          From Kickoff to Booked Meetings in Weeks.
        </h2>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((s, i) => (
          <article
            key={s.n}
            data-testid={`lg-step-${s.n}`}
            className="reveal relative bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <span className="text-[42px] font-bold leading-none text-[#16A06A]/20">
              {s.n}
            </span>
            <span className="eyebrow text-[#16A06A] block mt-3">Step {parseInt(s.n, 10)}</span>
            <h3 className="mt-2 text-[18px] font-semibold text-[#0A1628]">{s.title}</h3>
            <p className="mt-3 text-[14px] leading-relaxed text-slate-500">{s.body}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------- Why RevGen ------------------------------- */

const WHY = [
  {
    icon: Users,
    title: "A Dedicated Team",
    body:
      "Trained SDRs in U.S.-aligned time zones, fully managed by RevGen — not a faceless agency queue.",
  },
  {
    icon: Wrench,
    title: "Tooling Included",
    body:
      "Enrichment, sequencing, and deliverability tools are part of the engagement. No separate software bills to stitch together.",
  },
  {
    icon: LineChart,
    title: "Transparent Reporting",
    body:
      "Weekly metrics on activity, replies, and booked meetings so you always know what your pipeline investment is returning.",
  },
];

const WhyRevGen = () => (
  <section data-testid="lg-why" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Why RevGen</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          The <span className="text-[#16A06A]">RevGen Advantage.</span>
        </h2>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {WHY.map((w, i) => {
          const Icon = w.icon;
          return (
            <article
              key={w.title}
              data-testid={`lg-why-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={18} />
              </div>
              <h3 className="mt-5 text-[19px] font-semibold text-[#0A1628]">{w.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-500">{w.body}</p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* --------------------------------- Results -------------------------------- */

const RESULTS = [
  "More conversations with the accounts you actually want",
  "Protected sender reputation & deliverability",
  "Reps freed to focus on closing",
  "Predictable, reportable pipeline",
  "Lower cost per meeting vs. in-house SDRs",
];

const Results = () => (
  <section data-testid="lg-results" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Results</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Pipeline That <span className="text-[#16A06A]">Compounds.</span>
        </h2>
      </div>

      <ul className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {RESULTS.map((r, i) => (
          <li
            key={r}
            data-testid={`lg-result-${i + 1}`}
            className="reveal group flex items-start gap-4 bg-white rounded-2xl border border-slate-200 p-6 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#16A06A] text-white shadow-[0_6px_18px_rgba(22,160,106,0.30)]">
              {i % 2 === 0 ? (
                <Award size={18} strokeWidth={2.5} />
              ) : (
                <Trophy size={18} strokeWidth={2.5} />
              )}
            </span>
            <span className="text-[16px] lg:text-[17px] font-semibold text-[#0A1628] leading-snug">
              {r}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ----------------------------------- FAQ ---------------------------------- */

const FAQS = [
  {
    q: "What does RevGen handle vs. my team?",
    a: "We handle targeting, list building, messaging, sending, and meeting booking. Your team takes the qualified meetings and closes.",
  },
  {
    q: "How fast will I see meetings?",
    a: "Most clients see first qualified meetings within about three weeks of launch, after list build and infrastructure warm-up.",
  },
  {
    q: "What channels do you use?",
    a: "Coordinated email, LinkedIn, and calling cadences tailored to your buyers.",
  },
  {
    q: "Do I need to provide tools?",
    a: "No. Enrichment, sequencing, and deliverability tooling are included in the engagement.",
  },
  {
    q: "How is this priced?",
    a: "Pricing depends on scope and target volume. Book a call and we'll scope it with you.",
  },
  {
    q: "How do you protect my domain reputation?",
    a: "We warm up dedicated sending infrastructure and monitor deliverability so your primary domain stays protected.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="lg-faq" className="py-24 lg:py-28 bg-[#F8FAFC]">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal">
            <Eyebrow tone="muted">FAQ</Eyebrow>
            <h2 className="h2-section mt-3 text-[#0A1628]">
              Lead Generation — Frequently Asked Questions.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-500">
              Still unsure?{" "}
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#16A06A] font-semibold hover:text-[#0E7C53]"
              >
                Book a 30-min call →
              </a>
            </p>
          </div>

          <div className="lg:col-span-8 reveal">
            <div className="rounded-2xl border border-slate-200 bg-white card-soft divide-y divide-slate-200 overflow-hidden">
              {FAQS.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div key={f.q} data-testid={`lg-faq-item-${i + 1}`}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      data-testid={`lg-faq-toggle-${i + 1}`}
                      className="w-full flex items-center justify-between gap-6 px-6 md:px-8 py-6 text-left hover:bg-[#FAFAFA] transition-colors"
                    >
                      <span className="text-[16px] md:text-[17px] font-semibold text-[#0A1628]">
                        {f.q}
                      </span>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                          isOpen
                            ? "bg-[#16A06A] text-white"
                            : "bg-[#E7F6EE] text-[#16A06A]"
                        }`}
                      >
                        {isOpen ? (
                          <Minus size={16} strokeWidth={3} />
                        ) : (
                          <Plus size={16} strokeWidth={3} />
                        )}
                      </span>
                    </button>
                    <div
                      className={`grid transition-all duration-300 ease-out ${
                        isOpen
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-6 md:px-8 pb-6 text-[15px] md:text-[16px] leading-relaxed text-slate-500">
                          {f.a}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------- Final CTA -------------------------------- */

const FinalCTA = () => (
  <section data-testid="lg-final-cta" className="relative bg-[#0A1628] overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 left-1/4 h-[360px] w-[360px] rounded-full bg-[#16A06A]/15 blur-3xl" />
      <div className="absolute -bottom-32 right-10 h-[420px] w-[420px] rounded-full bg-[#16A06A]/10 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-[1200px] px-6 lg:px-8 py-24 lg:py-28 text-center">
      <div className="reveal">
        <span className="eyebrow text-[#16A06A]">Get started</span>
        <h2 className="h2-section mt-3 text-white max-w-3xl mx-auto">
          Ready to <span className="text-[#34D399]">Fill Your Pipeline?</span>
        </h2>
        <p className="mt-5 text-[17px] lg:text-[18px] leading-relaxed text-slate-300 max-w-2xl mx-auto">
          Book a 30-minute strategy call and we'll map an outbound engine for
          your team.
        </p>
        <div className="mt-9 flex justify-center">
          <PrimaryCTA testid="lg-final-cta-btn" />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------------------------- Page ---------------------------------- */

export default function LeadGeneration() {
  useReveal();

  useEffect(() => {
    document.title = "Lead Generation | RevGen";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Done-for-you outbound lead generation from RevGen — targeting, messaging, multichannel outreach, and qualified meetings booked. Book a strategy call."
      );
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div data-testid="lead-generation-page" className="bg-white text-[#0A1628]">
      <Navbar />
      <main>
        <Hero />
        <Stats />
        <Problem />
        <WhatWeDo />
        <HowItWorks />
        <WhyRevGen />
        <Results />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
